import { BoxGeometry, CylinderGeometry, Group, Mesh } from 'three';
import type { DeckConfig, HouseBodyConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * A ground-level patio deck attached to one facade.
 *
 * Layout:
 *   - Slab: a thin floor pad sized `length × depth × THICKNESS`, with its
 *     inboard edge flush against the host facade and its outboard edge
 *     extending `depth` away from the house.
 *   - Optional railing on the three open edges (left, far, right). The
 *     railing is a series of low posts joined by a top rail.
 *   - Optional descending steps from the deck top to grade. Only emitted
 *     when the body is on a plinth — otherwise the deck top sits at grade
 *     level already and steps would be moot.
 *
 * The deck's elevation matches the body's plinth top so you can step
 * straight from the house onto the deck without a kink.
 */

const DECK_SLAB_THICKNESS = 0.12;
const DECK_RAILING_HEIGHT = 0.95;
const DECK_RAILING_POST_RADIUS = 0.04;
const DECK_RAILING_POST_SPACING = 0.9;

export interface LotBounds {
  xLo: number;
  xHi: number;
  zLo: number;
  zHi: number;
}

export function buildDeck(
  body: HouseBodyConfig,
  deck: DeckConfig,
  floorTopY: number,
  lotBounds: LotBounds,
  kit: MaterialKit,
): Group {
  const group = new Group();
  group.name = 'Deck';

  // Clamp the deck's depth so the slab stays inside the lot, and decide
  // whether stairs still fit (each tread is `STEP_DEPTH` outboard of the
  // deck's far edge). If neither slab nor stairs fit, we degrade rather
  // than emit nothing so the door still has a small landing in front.
  const margin = 0.15;
  const STEP_DEPTH_OUT = 0.3;
  const stairsClearance = deck.hasStairsToGrade && floorTopY > 0.1
    ? Math.max(2, Math.round(floorTopY / 0.16)) * STEP_DEPTH_OUT
    : 0;
  const minDepth = 0.6;
  const requestedDepth = deck.depth;
  const availableOutward = availableOutboard(body, deck.side, lotBounds, margin);
  const usedDepth = Math.min(requestedDepth, Math.max(minDepth, availableOutward - stairsClearance));
  const stairsFit = stairsClearance > 0 && availableOutward - usedDepth >= stairsClearance;
  const effectiveDeck: DeckConfig = {
    ...deck,
    depth: Math.max(minDepth, usedDepth),
    hasStairsToGrade: stairsFit,
  };

  // Compute the deck's outer rectangle in world XZ.
  const facadeAxis = deckAxis(effectiveDeck.side);
  const sx = facadeAxis === 'x' ? effectiveDeck.length : effectiveDeck.depth;
  const sz = facadeAxis === 'x' ? effectiveDeck.depth : effectiveDeck.length;

  // Place the slab so its TOP is exactly the indoor floor top — crossing
  // the door is flush with no kink. We bias 1mm down to stay clear of
  // the floor slab's underside.
  const slabY = floorTopY - DECK_SLAB_THICKNESS / 2 - 0.001;

  // Place the slab so its inboard edge sits flush against the facade.
  const { centerX, centerZ } = computeDeckCenter(body, effectiveDeck, sx, sz);
  const slab = new Mesh(
    new BoxGeometry(sx, DECK_SLAB_THICKNESS, sz),
    kit.basePlate,
  );
  slab.name = 'DeckSlab';
  slab.castShadow = true;
  slab.receiveShadow = true;
  slab.position.set(centerX, slabY, centerZ);
  group.add(slab);

  if (effectiveDeck.hasRailing) {
    addRailing(group, body, effectiveDeck, kit, slabY + DECK_SLAB_THICKNESS / 2);
  }

  if (effectiveDeck.hasStairsToGrade && floorTopY > 0.1) {
    addStairsToGrade(group, body, effectiveDeck, kit, slabY + DECK_SLAB_THICKNESS / 2, floorTopY);
  }

  return group;
}

/** Distance from the body's facade outwards to the lot edge minus a
 *  small margin. Used to clamp deck depth + stair runs. */
function availableOutboard(
  body: HouseBodyConfig,
  side: DeckConfig['side'],
  lot: LotBounds,
  margin: number,
): number {
  switch (side) {
    case 'front':
      return Math.max(0, body.originZ - lot.zLo - margin);
    case 'back':
      return Math.max(0, lot.zHi - (body.originZ + body.footprintDepth) - margin);
    case 'left':
      return Math.max(0, body.originX - lot.xLo - margin);
    case 'right':
      return Math.max(0, lot.xHi - (body.originX + body.footprintWidth) - margin);
  }
}

function deckAxis(side: DeckConfig['side']): 'x' | 'z' {
  return side === 'front' || side === 'back' ? 'x' : 'z';
}

interface DeckCenter {
  centerX: number;
  centerZ: number;
}

/**
 * Position the deck slab so that its inboard edge sits flush against the
 * host facade. The deck offset is along the facade axis (lower-index
 * corner = 0); the deck depth extends away from the facade.
 */
function computeDeckCenter(body: HouseBodyConfig, deck: DeckConfig, sx: number, sz: number): DeckCenter {
  switch (deck.side) {
    case 'front': {
      // Front facade is at z = body.originZ. Deck extends to z < that.
      return {
        centerX: body.originX + deck.offset + sx / 2,
        centerZ: body.originZ - sz / 2,
      };
    }
    case 'back': {
      // Back facade at z = body.originZ + body.footprintDepth. The deck's
      // offset is measured from the back-right corner because doors on
      // 'back' use mirrored u-coords (see buildWalls' axis map).
      return {
        centerX: body.originX + body.footprintWidth - deck.offset - sx / 2,
        centerZ: body.originZ + body.footprintDepth + sz / 2,
      };
    }
    case 'left': {
      // Left facade at x = body.originX. Offset is measured from the
      // back-left corner; depth extends to x < originX.
      return {
        centerX: body.originX - sx / 2,
        centerZ: body.originZ + body.footprintDepth - deck.offset - sz / 2,
      };
    }
    case 'right': {
      // Right facade at x = body.originX + body.footprintWidth. Offset
      // measured from the front-right corner; depth extends to +x.
      return {
        centerX: body.originX + body.footprintWidth + sx / 2,
        centerZ: body.originZ + deck.offset + sz / 2,
      };
    }
  }
}

function addRailing(
  group: Group,
  body: HouseBodyConfig,
  deck: DeckConfig,
  kit: MaterialKit,
  topY: number,
): Group {
  // Build the railing in deck-local coords and then rotate/translate it
  // onto the slab. The deck's "open" edges are everything except the side
  // touching the facade, so we draw three lines: along the far edge plus
  // two perpendicular ends.
  const facadeAxis = deckAxis(deck.side);
  const sxLocal = deck.length;
  const szLocal = deck.depth;

  // Local coords: x along the facade axis, z away from the facade.
  // Deck slab spans x ∈ [-length/2, +length/2], z ∈ [0, depth].
  const localGroup = new Group();
  localGroup.name = 'DeckRailing';

  // Far edge (parallel to facade, at z = depth).
  addRailLine(localGroup, kit, -sxLocal / 2, sxLocal / 2, szLocal, true, topY);
  // Left end (perpendicular, at x = -length/2).
  addRailLine(localGroup, kit, -sxLocal / 2, -sxLocal / 2, szLocal, false, topY);
  // Right end (perpendicular, at x = +length/2).
  addRailLine(localGroup, kit, sxLocal / 2, sxLocal / 2, szLocal, false, topY);

  // Place the localGroup so its (0, _, 0) sits at the inboard-mid edge
  // of the slab, then rotate so +z (local) points away from the facade.
  const { centerX, centerZ, rotY } = railingPlacement(body, deck);
  localGroup.position.set(centerX, 0, centerZ);
  localGroup.rotation.y = rotY;
  group.add(localGroup);
  // Hide the extra layer; we keep it for tree clarity.
  if (facadeAxis === 'x') {
    /* nothing — the local frame already aligns with world. */
  }
  return localGroup;
}

interface RailingPlacement extends DeckCenter {
  rotY: number;
}

function railingPlacement(body: HouseBodyConfig, deck: DeckConfig): RailingPlacement {
  // The railing's local origin sits at the mid-point of the inboard edge
  // (where the deck meets the facade). Local +z points away from the house.
  switch (deck.side) {
    case 'front':
      return {
        centerX: body.originX + deck.offset + deck.length / 2,
        centerZ: body.originZ,
        rotY: Math.PI, // local +z (away) → world -z
      };
    case 'back':
      return {
        centerX: body.originX + body.footprintWidth - deck.offset - deck.length / 2,
        centerZ: body.originZ + body.footprintDepth,
        rotY: 0,
      };
    case 'left':
      return {
        centerX: body.originX,
        centerZ: body.originZ + body.footprintDepth - deck.offset - deck.length / 2,
        rotY: -Math.PI / 2,
      };
    case 'right':
      return {
        centerX: body.originX + body.footprintWidth,
        centerZ: body.originZ + deck.offset + deck.length / 2,
        rotY: Math.PI / 2,
      };
  }
}

function addRailLine(
  parent: Group,
  kit: MaterialKit,
  x0: number,
  x1: number,
  zPos: number,
  alongX: boolean,
  topY: number,
): void {
  const length = alongX
    ? Math.abs(x1 - x0)
    : zPos; // perpendicular line spans z ∈ [0, depth]
  const cx = alongX ? (x0 + x1) / 2 : x0;
  const cz = alongX ? zPos : zPos / 2;

  // Top rail.
  const railGeo = new BoxGeometry(
    alongX ? length : 0.04,
    0.04,
    alongX ? 0.04 : length,
  );
  const rail = new Mesh(railGeo, kit.fence);
  rail.castShadow = true;
  rail.receiveShadow = true;
  rail.position.set(cx, topY + DECK_RAILING_HEIGHT, cz);
  parent.add(rail);

  // Posts every DECK_RAILING_POST_SPACING along the line.
  const totalLen = alongX ? length : length;
  const postCount = Math.max(2, Math.ceil(totalLen / DECK_RAILING_POST_SPACING) + 1);
  for (let i = 0; i < postCount; i++) {
    const t = postCount === 1 ? 0.5 : i / (postCount - 1);
    const px = alongX ? x0 + (x1 - x0) * t : x0;
    const pz = alongX ? zPos : t * length;
    const post = new Mesh(
      new CylinderGeometry(
        DECK_RAILING_POST_RADIUS,
        DECK_RAILING_POST_RADIUS,
        DECK_RAILING_HEIGHT,
        8,
      ),
      kit.fence,
    );
    post.castShadow = true;
    post.receiveShadow = true;
    post.position.set(px, topY + DECK_RAILING_HEIGHT / 2, pz);
    parent.add(post);
  }
}

function addStairsToGrade(
  group: Group,
  body: HouseBodyConfig,
  deck: DeckConfig,
  kit: MaterialKit,
  deckTopY: number,
  totalRise: number,
): void {
  const stepCount = Math.max(2, Math.round(totalRise / 0.16));
  const stepRise = totalRise / stepCount;
  const stepDepth = 0.3;
  const stepWidth = Math.min(1.4, deck.length * 0.6);

  // Place the stair run centred on the outboard edge of the deck, climbing
  // back up toward the house (i.e. local +z direction). We position each
  // tread in world coords using `railingPlacement` to derive the deck's
  // local frame, then translate.
  const placement = railingPlacement(body, deck);
  const cosR = Math.cos(placement.rotY);
  const sinR = Math.sin(placement.rotY);

  for (let i = 0; i < stepCount; i++) {
    // Local-space tread position: at z = depth + (stepCount-1-i)*stepDepth + stepDepth/2
    // Bottom tread is furthest out; top tread sits flush with the deck.
    const localZ = deck.depth + (stepCount - 1 - i) * stepDepth + stepDepth / 2;
    const localX = 0;
    const worldX = placement.centerX + cosR * localX + sinR * localZ;
    const worldZ = placement.centerZ - sinR * localX + cosR * localZ;
    const treadY = deckTopY - (stepCount - i) * stepRise + stepRise / 2;

    const tread = new Mesh(
      new BoxGeometry(stepWidth, stepRise, stepDepth),
      kit.stair,
    );
    tread.castShadow = true;
    tread.receiveShadow = true;
    tread.position.set(worldX, treadY, worldZ);
    tread.rotation.y = placement.rotY;
    group.add(tread);
  }
}
